import React, { useCallback, useEffect, useState } from 'react';
import { aggregateWithFallback } from './localAggregate';
import {
  AMBER, PLUM, PLUM_LIGHT, TEAL,
  BarCard, HeatMapCard, LineCard, PieCard, inr, monthLabel, pctTick
} from './charts';
import styles from './NativeVisuals.module.css';

const n = (value) => {
  const x = Number(value);
  return Number.isFinite(x) ? x : 0;
};

/** Runs several aggregate queries for one template and reports them together. */
function useAggregates(buildSpecs, key, filters) {
  const [state, setState] = useState({ status: 'loading' });
  const filterKey = JSON.stringify(filters || {});

  const run = useCallback(
    (signal) => {
      setState({ status: 'loading' });
      const specs = buildSpecs(JSON.parse(filterKey));
      Promise.all(specs.map((spec) => aggregateWithFallback(spec, { signal })))
        .then((results) => {
          if (signal?.aborted) return;
          setState({
            status: 'ready',
            results: results.map((r) => r.rows || []),
            // A grouped result only truncates when a breakdown has more
            // buckets than the cap, which narrowing the filters fixes.
            truncated: results.some((r) => r.truncated),
            sample: (results.find((r) => r.sample) || {}).sample || null
          });
        })
        .catch((error) => {
          if (signal?.aborted || error.name === 'AbortError') return;
          setState({ status: 'error', message: error.message || 'Could not load these figures.' });
        });
    },
    // buildSpecs is rebuilt each render, so key on the template and filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, filterKey]
  );

  useEffect(() => {
    const controller = new AbortController();
    run(controller.signal);
    return () => controller.abort();
  }, [run]);

  return [state, () => run()];
}

const Kpis = ({ items }) => (
  <div className={styles.kpiRow}>
    {items.map((kpi) => (
      <div key={kpi.label} className={styles.kpi}>
        <span className={styles.kpiLabel}>{kpi.label}</span>
        <span className={styles.kpiValue}>{kpi.value}</span>
      </div>
    ))}
  </div>
);

const Caveat = ({ title, points }) => (
  <div className={styles.caveat}>
    <h4 className={styles.caveatTitle}>{title}</h4>
    <ul>{points.map((p, i) => <li key={i}>{p}</li>)}</ul>
  </div>
);

const Sampled = ({ shown, total }) => (
  <div className={styles.sampled}>
    <strong>These charts are drawn from a sample.</strong> {shown.toLocaleString()} of
    {' '}{total.toLocaleString()} records were read, spread evenly across the table, because
    the whole of it cannot be fetched to the browser. The shapes and proportions
    are representative; the totals and counts are not — they are roughly a
    {' '}{Math.max(Math.round(total / Math.max(shown, 1)), 2)}th of the real figures. Deploying the
    aggregate endpoint replaces this with the real numbers.
  </div>
);

const Truncated = () => (
  <div className={styles.truncated}>
    One of these breakdowns has more categories than can be charted at once, so
    it is showing only part of the picture. Narrow the filters above to see all
    of it.
  </div>
);

const Shell = ({ state, retry, label, children }) => {
  if (state.status === 'loading') {
    return (
      <div className={styles.canvas} data-export-canvas>
        <div className={styles.state}>
          <h2 className={styles.stateTitle}>Loading {label}…</h2>
          <p className={styles.stateText}>Working out the totals on the server.</p>
        </div>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className={styles.canvas} data-export-canvas>
        <div className={`${styles.state} ${styles.errorState}`}>
          <h2 className={styles.stateTitle}>{label} could not be loaded</h2>
          <p className={styles.stateText}>{state.message}</p>
          <button type="button" className={styles.retry} onClick={retry}>Try again</button>
        </div>
      </div>
    );
  }
  return children(state.results);
};

/* ------------------------------------------------------------------ */

const ATTENDANCE_SPECS = (filters) => [
  {
    table: 'Attendance',
    filters,
    groupBy: [{ column: 'date', bucket: 'month' }, 'attended'],
    metrics: [{ fn: 'count', as: 'n' }],
    orderBy: [{ column: 'date', direction: 'asc' }],
    limit: 400
  },
  {
    table: 'Attendance',
    filters,
    groupBy: ['attended'],
    metrics: [{ fn: 'count', as: 'n' }],
    limit: 10
  }
];

export const AttendancePanel = ({ filters }) => {
  const [state, retry] = useAggregates(ATTENDANCE_SPECS, 'Attendance', filters);

  return (
    <>
    <Shell state={state} retry={retry} label="Attendance">
      {([monthly, overall]) => {
        const byMonth = new Map();
        monthly.forEach((row) => {
          const month = String(row.date);
          if (!byMonth.has(month)) byMonth.set(month, { month, present: 0, absent: 0 });
          const bucket = byMonth.get(month);
          if (row.attended === true || row.attended === 't') bucket.present += n(row.n);
          else bucket.absent += n(row.n);
        });
        const series = [...byMonth.values()]
          .sort((a, b) => a.month.localeCompare(b.month))
          .map((m) => ({
            ...m,
            total: m.present + m.absent,
            rate: m.present + m.absent ? Math.round((m.present / (m.present + m.absent)) * 1000) / 10 : 0
          }));

        let present = 0;
        let absent = 0;
        overall.forEach((row) => {
          if (row.attended === true || row.attended === 't') present += n(row.n);
          else absent += n(row.n);
        });
        const total = present + absent;
        const rate = total ? Math.round((present / total) * 1000) / 10 : 0;

        return (
          <>
            {state.sample ? <Sampled {...state.sample} /> : null}
            {state.truncated ? <Truncated /> : null}
            <div className={styles.canvas} data-export-canvas>
            <Kpis items={[
              { label: 'Records', value: total.toLocaleString() },
              { label: 'Marked present', value: present.toLocaleString() },
              { label: 'Marked absent', value: absent.toLocaleString() },
              { label: 'Present rate', value: `${rate}%` }
            ]} />

            <div className={styles.grid} style={{ '--cols': 2 }}>
              <LineCard
                wide
                title="Present rate by month"
                note="Share of records marked present. Read the caution below before acting on it."
                data={series}
                xKey="month"
                yTickFormatter={pctTick}
                tooltipFormatter={(v) => `${v}%`}
                series={[{ key: 'rate', name: 'Present rate', colour: PLUM }]}
                area
              />
              <BarCard
                title="Present and absent by month"
                note="Record counts, stacked."
                data={series}
                xKey="month"
                yTickFormatter={(v) => v.toLocaleString()}
                tooltipFormatter={(v) => v.toLocaleString()}
                series={[
                  { key: 'present', name: 'Present', colour: TEAL, stackId: 'a' },
                  { key: 'absent', name: 'Absent', colour: PLUM_LIGHT, stackId: 'a' }
                ]}
              />
              <PieCard
                title="Overall split"
                note="Every attendance record in the template."
                data={[
                  { name: 'Present', value: present },
                  { name: 'Absent', value: absent }
                ]}
                valueFormatter={(v) => v.toLocaleString()}
                centreValue={`${rate}%`}
                centreLabel="present"
              />
            </div>
            </div>

            <Caveat
              title="Treat this one with caution"
              points={[
                `Only ${rate}% of records are marked present. A school running at that rate would not be open, so the flag is far more likely to mean something other than "attended a class" — or to be recorded per period rather than per day.`,
                'Confirm what the attended column means in the source system before anyone reads these as attendance rates.',
                'The late flag is set on 10 records out of 63,303, and leave approval status is blank on all but one, so neither is charted.'
              ]}
            />
          </>
        );
      }}
    </Shell>
    </>
  );
};

/* ------------------------------------------------------------------ */

const ADMISSIONS_SPECS = (filters) => [
  {
    table: 'Admissions',
    filters,
    groupBy: [{ column: 'enquirydate', bucket: 'month' }],
    metrics: [{ fn: 'count', as: 'n' }],
    orderBy: [{ column: 'enquirydate', direction: 'asc' }],
    limit: 400
  },
  {
    table: 'Admissions',
    filters,
    groupBy: ['sourcename'],
    metrics: [{ fn: 'count', as: 'n' }],
    orderBy: [{ column: 'n', direction: 'desc' }],
    limit: 60
  },
  {
    table: 'Admissions',
    filters,
    groupBy: ['stagename'],
    metrics: [{ fn: 'count', as: 'n' }],
    orderBy: [{ column: 'n', direction: 'desc' }],
    limit: 30
  },
  {
    table: 'Admissions',
    filters,
    groupBy: ['sourcename', 'stagename'],
    metrics: [{ fn: 'count', as: 'n' }],
    limit: 900
  }
];

const TOP_SOURCES = 8;

export const AdmissionsPanel = ({ filters }) => {
  const [state, retry] = useAggregates(ADMISSIONS_SPECS, 'Admissions', filters);

  return (
    <>
    <Shell state={state} retry={retry} label="Admissions">
      {([monthly, sources, stages, cross]) => {
        const months = monthly
          .map((row) => ({ month: String(row.enquirydate), enquiries: n(row.n) }))
          .sort((a, b) => a.month.localeCompare(b.month));

        const ranked = sources
          .map((row) => ({ name: row.sourcename || 'Not stated', value: n(row.n) }))
          .sort((a, b) => b.value - a.value);
        const top = ranked.slice(0, TOP_SOURCES);
        const restTotal = ranked.slice(TOP_SOURCES).reduce((t, s) => t + s.value, 0);
        const slices = restTotal ? [...top, { name: 'Everything else', value: restTotal }] : top;

        const stageRows = stages
          .map((row) => ({ stage: row.stagename || 'Not stated', n: n(row.n) }))
          .sort((a, b) => b.n - a.n);

        const totalEnquiries = ranked.reduce((t, s) => t + s.value, 0);
        const admitted = stageRows.find((s) => /admission taken/i.test(s.stage));
        const conversion = totalEnquiries && admitted
          ? Math.round((admitted.n / totalEnquiries) * 1000) / 10
          : 0;

        // Conversion by source: share of each source's enquiries that reached
        // "Admission Taken". That is the question the mix chart cannot answer.
        const topNames = top.map((s) => s.name);
        const stageNames = ['Enquiry', 'Application Taken', 'Admission Offered', 'Admission Taken', 'Enquiry Cancelled'];
        const grid = new Map();
        cross.forEach((row) => {
          const source = row.sourcename || 'Not stated';
          const stage = row.stagename || 'Not stated';
          grid.set(`${source}|${stage}`, n(row.n));
        });

        return (
          <>
            {state.sample ? <Sampled {...state.sample} /> : null}
            {state.truncated ? <Truncated /> : null}
            <div className={styles.canvas} data-export-canvas>
            <Kpis items={[
              { label: 'Enquiries', value: totalEnquiries.toLocaleString() },
              { label: 'Admissions taken', value: (admitted ? admitted.n : 0).toLocaleString() },
              { label: 'Conversion', value: `${conversion}%` },
              { label: 'Sources', value: ranked.length }
            ]} />

            <div className={styles.grid} style={{ '--cols': 2 }}>
              <LineCard
                wide
                title="Enquiries by month"
                note="When enquiries arrive. The admissions cycle shows clearly."
                data={months}
                xKey="month"
                yTickFormatter={(v) => v.toLocaleString()}
                tooltipFormatter={(v) => `${v.toLocaleString()} enquiries`}
                series={[{ key: 'enquiries', name: 'Enquiries', colour: PLUM }]}
                area
              />
              <PieCard
                title="Where enquiries come from"
                note={`Top ${TOP_SOURCES} of ${ranked.length} sources; the rest are grouped.`}
                data={slices}
                valueFormatter={(v) => v.toLocaleString()}
                centreValue={totalEnquiries.toLocaleString()}
                centreLabel="enquiries"
              />
              <BarCard
                title="How far enquiries get"
                note="Every enquiry sits at exactly one stage."
                data={stageRows}
                xKey="stage"
                layout="vertical"
                categoryWidth={140}
                yTickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
                tooltipFormatter={(v) => v.toLocaleString()}
                series={[{ key: 'n', name: 'Enquiries', colour: PLUM }]}
              />
              <HeatMapCard
                title="Source against stage"
                note="How many enquiries from each source reached each stage. Darker is more."
                rows={topNames}
                columns={stageNames}
                valueAt={(source, stage) => {
                  const v = grid.get(`${source}|${stage}`);
                  return v === undefined ? null : v;
                }}
                formatValue={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)}
              />
            </div>
            </div>
          </>
        );
      }}
    </Shell>
    </>
  );
};

/* ------------------------------------------------------------------ */

const PAYMENTS_SPECS = (filters) => [
  {
    table: 'Payments',
    filters,
    groupBy: [{ column: 'paymentdate', bucket: 'month' }],
    metrics: [{ fn: 'sum', column: 'amount', as: 'amount' }, { fn: 'count', as: 'n' }],
    orderBy: [{ column: 'paymentdate', direction: 'asc' }],
    limit: 400
  },
  {
    table: 'Payments',
    filters,
    groupBy: ['mode'],
    metrics: [{ fn: 'sum', column: 'amount', as: 'amount' }, { fn: 'count', as: 'n' }],
    orderBy: [{ column: 'amount', direction: 'desc' }],
    limit: 30
  }
];

export const PaymentsPanel = ({ filters }) => {
  const [state, retry] = useAggregates(PAYMENTS_SPECS, 'Payments', filters);

  return (
    <>
    <Shell state={state} retry={retry} label="Payments">
      {([monthly, modes]) => {
        const months = monthly
          .map((row) => ({
            month: String(row.paymentdate),
            amount: n(row.amount),
            count: n(row.n)
          }))
          .sort((a, b) => a.month.localeCompare(b.month));

        const byMode = modes
          .map((row) => ({ name: row.mode || 'Not stated', value: n(row.amount), count: n(row.n) }))
          .sort((a, b) => b.value - a.value);

        const total = byMode.reduce((t, m) => t + m.value, 0);
        const transactions = byMode.reduce((t, m) => t + m.count, 0);
        const best = months.reduce((b, m) => (m.amount > (b?.amount || 0) ? m : b), null);

        return (
          <>
            {state.sample ? <Sampled {...state.sample} /> : null}
            {state.truncated ? <Truncated /> : null}
            <div className={styles.canvas} data-export-canvas>
            <Kpis items={[
              { label: 'Collected', value: inr(total) },
              { label: 'Transactions', value: transactions.toLocaleString() },
              { label: 'Average payment', value: inr(transactions ? total / transactions : 0) },
              { label: 'Best month', value: best ? monthLabel(best.month) : '—' }
            ]} />

            <div className={styles.grid} style={{ '--cols': 2 }}>
              <LineCard
                wide
                title="Collections by month"
                note="Money received, by the date it was paid."
                data={months}
                xKey="month"
                yTickFormatter={inr}
                tooltipFormatter={(v) => inr(v)}
                series={[{ key: 'amount', name: 'Collected', colour: PLUM }]}
                area
              />
              <PieCard
                title="How parents pay"
                note="Share of the money, not of the transactions."
                data={byMode}
                valueFormatter={inr}
                centreValue={inr(total)}
                centreLabel="collected"
              />
              <BarCard
                title="Transactions by method"
                note="Volume rather than value — the two do not rank the same."
                data={byMode.map((m) => ({ mode: m.name, count: m.count }))}
                xKey="mode"
                layout="vertical"
                categoryWidth={150}
                yTickFormatter={(v) => v.toLocaleString()}
                tooltipFormatter={(v) => `${v.toLocaleString()} payments`}
                series={[{ key: 'count', name: 'Payments', colour: TEAL }]}
              />
            </div>
            </div>
          </>
        );
      }}
    </Shell>
    </>
  );
};

/* ------------------------------------------------------------------ */

const RECEIVABLES_SPECS = (filters) => [
  {
    table: 'Receivables',
    filters,
    groupBy: ['feetype'],
    metrics: [
      { fn: 'sum', column: 'amount', as: 'billed' },
      { fn: 'sum', column: 'amountpaid', as: 'paid' },
      { fn: 'sum', column: 'amountdue', as: 'due' }
    ],
    orderBy: [{ column: 'billed', direction: 'desc' }],
    limit: 60
  },
  {
    table: 'Receivables',
    filters,
    groupBy: [{ column: 'installmentdate', bucket: 'month' }],
    metrics: [
      { fn: 'sum', column: 'amount', as: 'billed' },
      { fn: 'sum', column: 'amountdue', as: 'due' }
    ],
    orderBy: [{ column: 'installmentdate', direction: 'asc' }],
    limit: 400
  }
];

const TOP_FEES = 8;

export const ReceivablesPanel = ({ filters }) => {
  const [state, retry] = useAggregates(RECEIVABLES_SPECS, 'Receivables', filters);

  return (
    <>
    <Shell state={state} retry={retry} label="Receivables">
      {([fees, schedule]) => {
        const ranked = fees
          .map((row) => ({
            feetype: row.feetype || 'Not stated',
            billed: n(row.billed),
            paid: n(row.paid),
            due: n(row.due)
          }))
          .sort((a, b) => b.billed - a.billed);

        const top = ranked.slice(0, TOP_FEES);
        const rest = ranked.slice(TOP_FEES);
        const restBilled = rest.reduce((t, f) => t + f.billed, 0);
        const restDue = rest.reduce((t, f) => t + f.due, 0);

        const billed = ranked.reduce((t, f) => t + f.billed, 0);
        const paid = ranked.reduce((t, f) => t + f.paid, 0);
        const due = ranked.reduce((t, f) => t + f.due, 0);
        const collected = billed ? Math.round((paid / billed) * 1000) / 10 : 0;

        const months = schedule
          .map((row) => ({
            month: String(row.installmentdate),
            billed: n(row.billed),
            due: n(row.due)
          }))
          .sort((a, b) => a.month.localeCompare(b.month));

        const dueSlices = restDue
          ? [...top.map((f) => ({ name: f.feetype, value: f.due })), { name: 'Everything else', value: restDue }]
          : top.map((f) => ({ name: f.feetype, value: f.due }));

        return (
          <>
            {state.sample ? <Sampled {...state.sample} /> : null}
            {state.truncated ? <Truncated /> : null}
            <div className={styles.canvas} data-export-canvas>
            <Kpis items={[
              { label: 'Billed', value: inr(billed) },
              { label: 'Received', value: inr(paid) },
              { label: 'Outstanding', value: inr(due) },
              { label: 'Collected', value: `${collected}%` }
            ]} />

            <div className={styles.grid} style={{ '--cols': 2 }}>
              <BarCard
                wide
                title="Billed against outstanding, by fee type"
                note={`Top ${TOP_FEES} of ${ranked.length} fee types${restBilled ? `; the rest total ${inr(restBilled)}` : ''}.`}
                data={top}
                xKey="feetype"
                angled
                yTickFormatter={inr}
                tooltipFormatter={(v) => inr(v)}
                series={[
                  { key: 'billed', name: 'Billed', colour: PLUM_LIGHT },
                  { key: 'due', name: 'Outstanding', colour: PLUM }
                ]}
              />
              <LineCard
                title="The billing schedule"
                note="When installments fall due across the year."
                data={months}
                xKey="month"
                yTickFormatter={inr}
                tooltipFormatter={(v) => inr(v)}
                series={[
                  { key: 'billed', name: 'Billed', colour: PLUM },
                  { key: 'due', name: 'Outstanding', colour: AMBER }
                ]}
              />
              <PieCard
                title="Where the outstanding money sits"
                note="Share of everything still unpaid."
                data={dueSlices}
                valueFormatter={inr}
                centreValue={inr(due)}
                centreLabel="outstanding"
              />
            </div>
            </div>

            <Caveat
              title="Worth knowing about this data"
              points={[
                `Only ${collected}% of what is billed shows as received here, but the schedule runs to March 2027 — most of this is billed ahead rather than overdue. Read the schedule chart alongside the totals.`,
                'The Payments template records ₹4.37Cr received over the past year, which does not reconcile with the ₹8.9L shown as paid here. The two cover different periods and probably different ledgers; worth confirming which one answers "what have we collected".',
                `There are ${ranked.length} distinct fee types, several of them prior-year dues spelled out in the name. Consolidating those in the source would make every report simpler.`
              ]}
            />
          </>
        );
      }}
    </Shell>
    </>
  );
};
